import { useState, useEffect, useCallback, useMemo } from 'react';
import { TableSortDirection } from '@spotoninc/nexus-react';
import taskManagerService from './taskManager.service';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationData } from '../../../../redux/slices/locationSlice';
import { mergeUsersToTasks } from '../tasks.utils';
import {
  extractIntegrationIds,
  extractLocationIds,
} from '../../../../utils/integrationUtils';

export const PRIORITY = [
  {
    label: 'High',
    value: 'high',
  },
  {
    label: 'Medium',
    value: 'medium',
  },
  {
    label: 'Low',
    value: 'low',
  },
];

export const STATUS = [
  {
    label: 'To Do',
    value: 'todo',
  },
  {
    label: 'In Progress',
    value: 'in-progress',
  },
  {
    label: 'Completed',
    value: 'completed',
  },
  {
    label: 'Cancelled',
    value: 'cancelled',
  },
];

export const TASK_TYPE = [
  {
    label: 'General',
    value: 'general',
  },
  {
    label: 'Summary',
    value: 'summary',
  },
  {
    label: 'Error',
    value: 'error',
  },
  {
    label: 'Improvement',
    value: 'improvement',
  },
];
/**
 * Custom hook that contains logic and api calls for task manager
 *
 * Parent
 *      ImprovedTaskManager
 */
export const useImprovedTaskManager = () => {
  const dispatch = useDispatch();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskModal, setTaskModal] = useState({
    show: false,
    payload: null, // null for new task, object for edit task
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const locationData = useSelector(
    state => state.locationSelectFormat.locationData
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [sort, setSort] = useState({ due_on: TableSortDirection.descending });

  const handleOpenMobileFilters = useCallback(() => {
    setIsMobileFiltersOpen(true);
  }, []);

  const handleCloseMobileFilters = useCallback(() => {
    setIsMobileFiltersOpen(false);
  }, []);

  const tasksWithUserInfo = useMemo(() => {
    return mergeUsersToTasks(tasks, users);
  }, [tasks, users]);

  // Helper function to convert location filter to integration_ids and location_ids
  const convertFiltersForApi = useCallback(
    filtersToConvert => {
      const apiFilters = { ...filtersToConvert };
      if (apiFilters['location']) {
        const locationValue = Array.isArray(apiFilters['location'])
          ? [...apiFilters['location']]
          : [apiFilters['location']];
        const integrationIds = extractIntegrationIds(locationValue, locationData);
        const locationIds = extractLocationIds(locationValue, locationData);

        // If no matching integration or location IDs were found, log a warning
        if (integrationIds.length === 0 && locationIds.length === 0) {
          console.warn(
            'convertFiltersForApi: location filter could not be resolved to any integration_ids or location_ids and will be ignored.',
            { location: locationValue }
          );
        }

        // Conditionally add integration_ids and/or location_ids based on selection
        if (integrationIds.length > 0) {
          apiFilters['integration_ids'] = integrationIds;
        }
        if (locationIds.length > 0) {
          apiFilters['location_ids'] = locationIds;
        }

        // Remove the 'location' key from API filters
        delete apiFilters['location'];
      }
      return apiFilters;
    },
    [locationData]
  );

  // Fetch tasks from the service
  const fetchTasks = useCallback(
    async ({ filters = {}, sortType = 'desc', page = 0, sort = {} }) => {
      try {
        setLoading(true);
        // Convert location filter to integration_ids and location_ids before API call
        const apiFilters = convertFiltersForApi(filters);
        const tasksData = await taskManagerService.fetchTasks({
          filters: apiFilters,
          page,
          sort,
        });
        setTasks(tasksData.data);
        setTotalPages(tasksData.total_pages);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    },
    [convertFiltersForApi]
  );

  // Fetch users from the service
  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await taskManagerService.fetchUsers();
      if (Array.isArray(usersData)) {
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Handle task deletion
  const handleDelete = useCallback(
    async (taskId, version, taskTitle = '') => {
      try {
        await taskManagerService.deleteTask(taskId, version, taskTitle);
        fetchTasks({ filters, sort });
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    },
    [fetchTasks, sort, filters]
  );

  const handleUpdateFilter = useCallback(
    key => value => {
      let updatedFilters;

      if (!value || value.length === 0) {
        updatedFilters = { ...filters };
        delete updatedFilters[key];
      } else if (value.length === 1) {
        if (filters.hasOwnProperty(key) && filters[key] === value[0]) {
          updatedFilters = { ...filters };
          delete updatedFilters[key];
        } else {
          updatedFilters = { ...filters, [key]: value[0] };
        }
      } else {
        updatedFilters = { ...filters, [key]: value };
      }

      setFilters(updatedFilters);
      // fetchTasks will automatically convert location to integration_ids/location_ids
      fetchTasks({ filters: updatedFilters, sort });
    },
    [filters, sort, fetchTasks]
  );

  // Consolidated functions to reduce multiple state calls
  const openAddModal = useCallback(() => {
    setTaskModal({
      show: true,
      payload: null, // null indicates new task
    });
  }, []);

  const openEditModal = useCallback(task => {
    setTaskModal({
      show: true,
      payload: task, // edit mode
    });
  }, []);

  const closeTaskModal = useCallback(() => {
    setTaskModal({
      show: false,
      payload: null,
    });
  }, []);

  const handleSort = useCallback(
    (column, initialDirection = TableSortDirection.ascending) => {
      const newSort = { ...sort };
      // check key exist in sort
      if (newSort[column]) {
        newSort[column] =
          newSort[column] === TableSortDirection.ascending
            ? TableSortDirection.descending
            : TableSortDirection.ascending;
      } else {
        newSort[column] = initialDirection;
      }
      setSort(newSort);
      fetchTasks({ filters, sort: newSort, currentPage });
    },
    [sort, filters, currentPage, fetchTasks]
  );

  // Function to open the drawer with the selected task
  const openTaskDrawer = useCallback(task => {
    setSelectedTask(task);
    setIsDrawerOpen(true);
  }, []);

  // Function to close the drawer
  const closeTaskDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  // Function to handle edit click
  const handleEditClick = useCallback(task => {
    setSelectedTask(task);
    setTaskModal({
      show: true,
      payload: task,
    });
  }, []);

  // Create debounced search function - use useMemo to prevent recreation
  const debounceSearch = useMemo(
    () =>
      debounce(searchFilters => {
        fetchTasks({ filters: searchFilters });
      }, 700),
    [fetchTasks]
  );

  const handleSearch = useCallback(
    e => {
      const value = e.target.value;
      // Update search term state immediately for UI feedback
      // setSearchTerm(value);

      // Create search filters combining current filters with search term
      const searchFilters = { ...filters };

      if (value && value.trim() !== '') {
        searchFilters.searchTerm = value.trim();
      } else {
        // Remove searchTerm if value is empty
        delete searchFilters.searchTerm;
      }

      // Call debounced search with combined filters
      debounceSearch(searchFilters);
    },
    [filters, debounceSearch]
  );

  // Handle form success (refresh data)
  const postFormSuccess = useCallback(() => {
    fetchTasks({ filters, sort });
  }, [fetchTasks, sort, filters]);

  // Similar to componentDidMount
  useEffect(() => {
    const fetchLocationAndSaveToRedux = async () => {
      // Only fetch locations if Redux doesn't already have them
      // This prevents overwriting the complex format (with integration-level options)
      // that comes from the overview page's LocationDropDown component
      if (locationData.length === 0) {
        try {
          const locations = await taskManagerService.fetchLocations();
          dispatch(setLocationData(locations));
        } catch (error) {
          console.error('Error fetching locations:', error);
        }
      }
    };
    fetchLocationAndSaveToRedux();
    fetchUsers();
    // Fetch tasks with default sort
    fetchTasks({
      filters: {},
      sort: { due_on: TableSortDirection.descending },
    });
    // Note: locationData is intentionally NOT in the dependency array to avoid infinite loops
    // The check inside the effect is sufficient to prevent unnecessary fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUsers, fetchTasks, dispatch]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const userOptions = useMemo(() => {
    return users.map(user => ({
      label: user.first_name,
      value: user.user_id_id,
    }));
  }, [users]);

  const handlePageChange = useCallback(
    page => {
      if (page < 1 || page > totalPages) {
        return;
      }
      setCurrentPage(page);
      fetchTasks({ page, filters, sort });
    },
    [totalPages, filters, sort, fetchTasks]
  );

  const handleOldFetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      await fetchTasks({
        filters,
        sort,
        page: currentPage,
      });
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, sort, fetchTasks]);

  return {
    isMobileFiltersOpen,
    tasks: tasksWithUserInfo,
    users,
    loading,
    taskModal,
    selectedTask,
    isDrawerOpen,
    userOptions,
    filters,
    currentPage,
    locationData,
    selectedTaskVersion: selectedTask?.version || '1',
    totalPages,
    sort,

    fetchTasks,
    postFormSuccess,
    handleSort,
    handleDelete,
    handleEditClick,
    openTaskDrawer,
    closeTaskDrawer,
    handleUpdateFilter,
    handleSearch,
    handleOldFetchTasks,
    handleOpenMobileFilters,
    handleCloseMobileFilters,

    openAddModal,
    openEditModal,
    closeTaskModal,
    handlePageChange,
  };
};

export default useImprovedTaskManager;
